import { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, IconButton, Icon, Input, Select, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";

type Team = { id: string; name: string; memberCount: number };
type Member = { id: string; displayName: string; jobTitle: string | null };
type Candidate = { id: string; displayName: string; isActive: boolean };

const PAGE = 50;

/** One team: its members, and a control to add someone from the office. */
function TeamCard({ team, candidates, onDelete, onChanged }: { team: Team; candidates: Candidate[]; onDelete: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pick, setPick] = useState("");

  const fail = useCallback(
    (fallback: string) => (error: unknown) => toast(error instanceof ApiError ? error.message : fallback, { tone: "danger" }),
    [toast],
  );

  const load = useCallback(
    (offset: number) => {
      setLoading(true);
      api
        .get<{ members: Member[]; total: number; hasMore: boolean }>(`/teams/${team.id}/members?limit=${PAGE}&offset=${offset}`)
        .then((res) => {
          setMembers((prev) => (offset === 0 ? res.members : [...prev, ...res.members]));
          setTotal(res.total);
          setHasMore(res.hasMore);
        })
        .catch(fail("Could not load the members."))
        .finally(() => setLoading(false));
    },
    [team.id, fail],
  );

  useEffect(() => {
    if (open) load(0);
  }, [open, load]);

  const add = () => {
    if (!pick) return;
    api
      .post(`/teams/${team.id}/members`, { userId: pick })
      .then(() => {
        setPick("");
        load(0);
        onChanged();
      })
      .catch(fail("Could not add that person."));
  };

  const remove = (userId: string) => {
    api
      .delete(`/teams/${team.id}/members/${userId}`)
      .then(() => {
        setMembers((prev) => prev.filter((m) => m.id !== userId));
        setTotal((n) => Math.max(0, n - 1));
        onChanged();
      })
      .catch(fail("Could not remove that person."));
  };

  const inTeam = new Set(members.map((m) => m.id));
  const options = candidates.filter((c) => c.isActive && !inTeam.has(c.id));

  return (
    <div className="admin-team">
      <div className="admin-team__head">
        <button type="button" className="admin-team__toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <Icon name={open ? "chevron-down" : "chevron-right"} size={14} />
          <strong>{team.name}</strong>
          <span>{team.memberCount} {team.memberCount === 1 ? "member" : "members"}</span>
        </button>
        <IconButton icon={<Icon name="trash" size={14} />} label={`Delete ${team.name}`} variant="ghost" onClick={onDelete} />
      </div>
      {open && (
        <div className="admin-team__body">
          {loading && members.length === 0 ? (
            <SkeletonText lines={2} />
          ) : (
            <ul>
              {members.map((m) => (
                <li key={m.id}>
                  <span>{m.displayName}</span>
                  <IconButton icon={<Icon name="close" size={12} />} label={`Remove ${m.displayName}`} variant="ghost" onClick={() => remove(m.id)} />
                </li>
              ))}
            </ul>
          )}
          {members.length < total && hasMore && (
            <Button variant="secondary" size="sm" loading={loading} onClick={() => load(members.length)}>
              Load more
            </Button>
          )}
          <div className="admin-team__add">
            <Select aria-label={`Add to ${team.name}`} value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Add a person…</option>
              {options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </Select>
            <Button size="sm" disabled={!pick} onClick={add}>
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Teams for the active office: the audiences a feed post can be aimed at.
 * Office admins only; the API enforces it, this panel is just its front door.
 */
export function AdminTeams({ users }: { users: Candidate[] }) {
  const { toast } = useToast();
  const [teams, setTeams] = useState<Team[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(
    (offset: number) => {
      setLoading(true);
      api
        .get<{ teams: Team[]; total: number; hasMore: boolean }>(`/teams?limit=${PAGE}&offset=${offset}`)
        .then((res) => {
          setTeams((prev) => (offset === 0 ? res.teams : [...prev, ...res.teams]));
          setTotal(res.total);
          setHasMore(res.hasMore);
        })
        .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not load teams.", { tone: "danger" }))
        .finally(() => setLoading(false));
    },
    [toast],
  );

  useEffect(() => load(0), [load]);

  const create = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    api
      .post("/teams", { name: trimmed })
      .then(() => {
        setName("");
        load(0);
      })
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not create that team.", { tone: "danger" }))
      .finally(() => setCreating(false));
  };

  const remove = (team: Team) => {
    api
      .delete(`/teams/${team.id}`)
      .then(() => load(0))
      .catch((error: unknown) =>
        toast(error instanceof ApiError ? error.message : "Could not delete that team.", { tone: "danger" }),
      );
  };

  return (
    <section className="admin__panel" aria-label="Teams">
      <form
        className="admin-invite"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <Input label="Team name" placeholder="Design" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} required />
        <div className="admin-invite__actions">
          <Button type="submit" loading={creating} disabled={!name.trim() || creating}>
            Create team
          </Button>
        </div>
      </form>

      {loading && teams.length === 0 ? (
        <SkeletonText lines={3} />
      ) : teams.length === 0 ? (
        <EmptyState icon="people" title="No teams yet" description="Teams let a post reach only the people it is meant for." />
      ) : (
        <div className="admin-teams">
          {teams.map((team) => (
            <TeamCard key={team.id} team={team} candidates={users} onDelete={() => remove(team)} onChanged={() => load(0)} />
          ))}
          {hasMore && teams.length < total && (
            <Button variant="secondary" size="sm" loading={loading} onClick={() => load(teams.length)}>
              Load more
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
